// Hook to fetch the /device response

/*  Shared  */
import { Notify, Response } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';
import { TFunction } from 'i18next';

/*  React  */
import { useCallback, useEffect, useMemo, useState } from 'react';

const defaultObject = {};

export const useFetchDevice = ({
    clientId = null,
    parameters = defaultObject
}: {
    clientId?: string | null | undefined;
    key?: string;
    parameters?: Record<string, string | boolean | number>;
}) => {
    const [devices, setDevices] = useState(null);
    const [loading, setLoading] = useState(true);
    // calling refetchData will force a rerender on the hook
    const [shouldRefetchData, setShouldRefetch] = useState({});

    const refetchDevices = () => setShouldRefetch({});

    const params = useMemo(
        () =>
            clientId
                ? {
                      excludeExpired: true,
                      includeClients: true,
                      platformTenantId: clientId,
                      size: -1,
                      ...parameters
                  }
                : { ...parameters },
        [clientId, parameters]
    );

    useEffect(() => {
        const { CancelToken } = axios;
        const source = CancelToken.source();

        const fetchData = async () => {
            setLoading(true);
            try {
                const response = await axios.get('/device', {
                    cancelToken: source.token,
                    params
                });
                if (!Response.hasData(response)) {
                    debug.e('No device found');
                } else {
                    setDevices(Response.getData(response));
                }
            } catch (error) {
                debug.e('Unable to get device', error);
            }
            setLoading(false);
        };

        fetchData();

        return () => {
            // Cancel the request when the component unmounts
            source.cancel();
        };
    }, [params, shouldRefetchData]);

    return {
        devices,
        loading,
        refetchDevices
    };
};

export const useDeleteDevice = (t: TFunction<'translation', undefined>) => {
    const [saving, setSaving] = useState(false);

    const deleteDevice = useCallback(
        async ({
            clientId,
            deviceId
        }: {
            clientId?: string;
            deviceId: string;
        }) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const params: any = {};
            if (clientId) {
                params.platformTenantId = clientId;
            }
            try {
                setSaving(true);
                await axios.delete(`/device/${deviceId}`, { params });
                Notify.success(t('Devices.deletedSuccessfully'));
                setSaving(false);
                return true; // success = true
            } catch (err) {
                Notify.error(t('Devices.deleteFail'));
                setSaving(false);
                return false; // success = true
            }
        },
        [t]
    );

    return { deleteDevice, saving };
};
